import IconChevronDown from '@eduzz/houston-icons/ChevronDown';
import { Grid, Typography, makeStyles, CircularProgress, MenuItem, Select, Box } from '@material-ui/core';
import React, { useEffect, useState, useRef, ChangeEvent } from 'react';
import { getAllSavedLessons } from 'services/savedLessons';
import { ISavedLesson } from 'interface/savedLesson';
import noCertificate from 'assets/img/no-certificate.png';
import CenteredPage from 'components/Shared/CenteredPage';
import LessonList from './LessonList';
import IconSearch from '@eduzz/houston-icons/SearchOutline';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: 50,
    maxWidth: 1280,
    margin: 'auto',
    color: 'white',
  },
  filter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.common.white,
    cursor: 'pointer',
    marginLeft: 25,
    '& > p': {
      color: theme.palette.common.white,
      marginLeft: 10,
      fontSize: 14,
    },
  },
  filterContainer: {
    [theme.breakpoints.down('md')]: {
      padding: '0 25px',
    },
    [theme.breakpoints.down('xs')]: {
      marginBottom: 20,
    },
    '& *[data-expand=true]': {
      width: 280,
      '@media (max-width: 320px)': {
        width: '100%',
      },
    },
  },
  title: {
    color: theme.palette.common.white,
    fontWeight: 600,
    fontSize: 20,
    [theme.breakpoints.down('md')]: {
      marginLeft: 20,
    },
  },
  description: {
    fontSize: 14,
    color: theme.palette.common.white,
    marginTop: 20,
    maxWidth: 400,
    textAlign: 'center',
  },
  fallbackImage: {
    maxWidth: 500,
    marginTop: 100,
    marginBottom: 50,
    margin: 'auto',
    display: 'block',
    width: '100%',
    [theme.breakpoints.down('md')]: {
      padding: 50,
      marginTop: 50,
      marginBottom: 0,
    },
  },
  loader: {
    margin: '50px auto',
    display: 'block',
  },
  selectRoot: {
    marginRight: theme.spacing(-4),
    '& > div': {
      '&:after, &:before': {
        content: 'none !important',
      },
    },
  },
  selectMenu: {
    border: 'none !important',
    paddingRight: 0,
    paddingLeft: theme.spacing(6),
    fontSize: 14,
  },
  selectIcon: {
    top: 'calc(50% - 8px)',
    left: 0,
    color: theme.palette.common.white,
  },
  dropdownMenu: {
    background: theme.palette.dark[600],
    overflow: 'visible',
    '&:after': {
      content: "''",
      position: 'absolute',
      backgroundColor: 'transparent',
      borderLeft: '10px solid transparent',
      borderRight: '10px solid transparent',
      borderBottom: `10px solid ${theme.palette.dark[600]}`,
      top: -10,
      left: 20,
    },
    '& li': {
      color: theme.palette.common.white,
    },
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    background: theme.palette.safevideo.dark,
    borderRadius: theme.shape.borderRadius,
    height: 35,
    width: 94,
    transition: '0.3s ease',
    '&:hover': {
      width: 280,
      '@media (max-width: 320px)': {
        width: '100%',
      },
    },
    '& svg': {
      fill: theme.palette.common.white,
      marginBottom: theme.spacing(-1),
    },
    '& input': {
      background: theme.palette.safevideo.dark,
      color: theme.palette.common.white,
      borderWidth: '0px',
      paddingLeft: '0px !important',
      margin: '0px !important',
      height: '35px !important',
      shadowBox: 'none',
    },
  },
  input: {
    borderWidth: '0px !important',
    paddingLeft: '0px !important',
    margin: '0px !important',
    height: '35px !important',
    '&:focus': {
      boxShadow: 'none !important',
    },
  },
  whiteText: {
    display: 'flex',
    color: theme.palette.common.white,
  },
}));

const SavedLesson: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [savedLessons, setSavedLessons] = useState<ISavedLesson[]>([]);
  const [page, setPage] = useState<number>(1);
  const [endReached, setEndReached] = useState(false);
  const [currentSearch, setCurrentSearch] = useState<string>('');
  const [order, setOrder] = useState<string>('none');
  const { current: ordersOptions } = useRef([
    { key: '?order=ASC&orderBy=created_at', name: 'Mais recentes' },
    { key: '?order=DESC&orderBy=created_at', name: 'Mais antigos' },
    { key: '?order=ASC&orderBy=title', name: 'Alfabética A - Z' },
    { key: '?order=ASC&orderBy=course_title', name: 'Cursos' },
  ]);

  const classes = useStyles();
  const debounceRef = useRef<NodeJS.Timeout>(null);

  useEffect(() => {
    getAllSavedLessons({ search: currentSearch, order: order === 'none' ? undefined : order })
      .then(({ data, total_pages }) => {
        setPage(1);
        setEndReached(total_pages <= 1);
        setSavedLessons(data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [order, currentSearch]);

  const handleListUpdate = (page: number, reset: boolean = false) => {
    if (reset) {
      setEndReached(false);
      setSavedLessons([]);
      setPage(1);
    }

    if (endReached && !reset) return;

    setLoading(true);
    getAllSavedLessons({
      search: currentSearch,
      page,
      order: order === 'none' ? undefined : order,
    }).then(({ data, total_pages }) => {
      if (total_pages <= page) setEndReached(true);
      setSavedLessons((lessons) => [...lessons, ...data]);
      setLoading(false);
    });
  };

  const handleEndReached = () => {
    handleListUpdate(page + 1);
    setPage((prev) => prev + 1);
  };

  const debounceChangeText = (e: any) => {
    const search = e.target.value;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      setCurrentSearch(search);
      clearTimeout(debounceRef.current);
    }, 500);
  };

  const handleRemoveLessonSaved = (lessonId: number) => {
    const newArray = [...savedLessons];
    newArray.splice(
      savedLessons.findIndex((lessonSaved) => lessonSaved.id === lessonId),
      1
    );
    setSavedLessons(newArray);
  };

  const onChangeOrder = (event: ChangeEvent<{ value: string }>) => {
    setOrder(event.target.value);
  };

  return (
    <CenteredPage paddingTop={12}>
      <Grid className={classes.filterContainer} container justifyContent='flex-end'>
        <Grid>
          <div data-expand={currentSearch.length > 0} className={`${classes.inputContainer} old-input`}>
            <Box margin={2}>
              <IconSearch />
            </Box>
            <input
              className={classes.input}
              id='savedLesson-search'
              onChange={debounceChangeText}
              placeholder='Buscar por título da aula'
            />
          </div>
        </Grid>
        <Grid className={classes.filter}>
          <div className={classes.selectRoot}>
            <Select
              classes={{ select: classes.whiteText, selectMenu: classes.selectMenu, icon: classes.selectIcon }}
              value={order}
              MenuProps={{
                classes: { paper: classes.dropdownMenu },
                getContentAnchorEl: null,
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
              }}
              onChange={onChangeOrder}
              IconComponent={(props) => <IconChevronDown {...props} size={14} />}
            >
              <MenuItem value='none' disabled id='savedLesson-sortBy'>
                Exibir por
              </MenuItem>
              {ordersOptions.map((option) => (
                <MenuItem key={option.key} value={option.key} id='savedLesson-sortBy-item'>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </div>
        </Grid>
      </Grid>

      {savedLessons.length == 0 && !loading ? (
        <Grid container direction='column' alignItems='center'>
          <img src={noCertificate} alt='fallback' className={classes.fallbackImage} />
          <Typography className={classes.title} id='savedLesson-empty'>
            Todas suas aulas salvas aparecerão aqui...
          </Typography>
          <Typography className={classes.description}>
            Aqui você poderá acessar com facilidade todas suas aulas salvas.
          </Typography>
        </Grid>
      ) : (
        <Typography className={classes.title}>Veja suas aulas salvas</Typography>
      )}
      <LessonList
        handleEndReached={handleEndReached}
        handleRemoveLessonSaved={handleRemoveLessonSaved}
        loading={loading}
        savedLessons={savedLessons}
      />
      {loading && <CircularProgress size={45} color={'inherit'} className={classes.loader} />}
    </CenteredPage>
  );
};

export default SavedLesson;
